<script lang="ts" setup>
import icCheckGreen from '../../assets/img/icon/check-success.svg';
import icDot from '../../assets/img/icon/dot-password.svg';
import settings from '../../config/settings';
import AppTypography from './AppTypography.vue';

const { password = '' } = defineProps<{
  password?: string;
}>();

const appConfig = useRuntimeConfig();

const hasEightCharacters = computed(() => password.length >= Number(settings.passwordCriteria.minLength));
const hasMaxCharacters = computed(() => password.length <= Number(settings.passwordCriteria.maxLength));
const hasOneUppercase = computed(() => {
  const uppercaseMatches = password.match(/[A-Z]/g);
  return uppercaseMatches && uppercaseMatches.length >= Number(settings.passwordCriteria.minUppercase);
});

const hasOneLowercase = computed(() => {
  const lowercaseMatches = password.match(/[a-z]/g);
  return lowercaseMatches && lowercaseMatches.length >= Number(settings.passwordCriteria.minLowercase);
});
const hasSpecialCharacter = computed(() => {
  const specialCharMatches = password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/g);
  return specialCharMatches && specialCharMatches.length >= Number(settings.passwordCriteria.minSpecialCharacters);
});
const hasNumber = computed(() => /\d/.test(password));
</script>
<template>
  <v-sheet
    class="password-criteria d-flex flex-column"
    :class="{
      'password-criteria--eight-chars': hasEightCharacters,
      'password-criteria--max-chars': hasMaxCharacters,
      'password-criteria--one-uppercase': hasOneUppercase,
      'password-criteria--one-number': hasNumber,
    }"
  >
    <AppTypography type="caption-2" class="mb-2" style="color: #80808e; font-weight: 700; line-height: 24px"
      >PASSWORD MUST CONTAIN:</AppTypography
    >
    <div
      class="d-flex pa-o align-center"
      :class="{
        'password-checked': hasEightCharacters && hasMaxCharacters,
      }"
      data-testid="eigth-characters"
    >
      <img
        :src="hasEightCharacters && hasMaxCharacters ? icCheckGreen : icDot"
        :class="hasEightCharacters && hasMaxCharacters ? 'icon-check' : 'icon-dot'"
      />
      <AppTypography
        type="footnote-1"
        class="font-footnote ml-1 pa-1"
        :style="{
          color: hasEightCharacters && hasMaxCharacters ? '#458C03' : '#414156',
          fontWeight: '400',
          lineHeight: '24px',
        }"
      >
        At least {{ settings.passwordCriteria.minLength }} characters and up to
        {{ settings.passwordCriteria.maxLength }} characters.
      </AppTypography>
    </div>
    <div
      class="d-flex pa-o mt-1 align-center"
      :class="{
        'password-checked': hasOneUppercase && hasOneLowercase,
      }"
      data-testid="one-uppercase"
    >
      <img
        :src="hasOneUppercase && hasOneLowercase ? icCheckGreen : icDot"
        :class="hasOneUppercase && hasOneLowercase ? 'icon-check' : 'icon-dot'"
      />
      <AppTypography
        type="footnote-1"
        class="font-footnote ml-1 pa-1"
        :style="{
          color: hasOneUppercase && hasOneLowercase ? '#458C03' : '#414156',
          fontWeight: '400',
          lineHeight: '24px',
        }"
      >
        Must include both uppercase and lowercase letters.
      </AppTypography>
    </div>

    <div
      class="d-flex pa-o mt-1 align-center"
      :class="{
        'password-checked': hasNumber,
      }"
      data-testid="one-number"
    >
      <img :src="hasNumber ? icCheckGreen : icDot" :class="hasNumber ? 'icon-check' : 'icon-dot'" />
      <AppTypography
        type="footnote-1"
        class="font-footnote ml-1 pa-1"
        :style="{ color: hasNumber ? '#458C03' : '#414156', fontWeight: '400', lineHeight: '24px' }"
      >
        Must contain at least one numeric character (0-9).
      </AppTypography>
    </div>
    <div
      class="d-flex pa-o mt-1 align-center"
      :class="{
        'password-checked': hasSpecialCharacter,
      }"
      data-testid="one-special-character"
    >
      <img :src="hasSpecialCharacter ? icCheckGreen : icDot" :class="hasSpecialCharacter ? 'icon-check' : 'icon-dot'" />
      <AppTypography
        type="footnote-1"
        class="font-footnote ml-1 pa-1"
        :style="{ color: hasSpecialCharacter ? '#458C03' : '#414156', fontWeight: '400', lineHeight: '24px' }"
      >
        Must contain at least one special character (!@#$%^&*).
      </AppTypography>
    </div>
  </v-sheet>
</template>
<style lang="scss" scoped>
@import '@/assets/scss/partials/colors';

.password-criteria {
  background-color: #f7f7f8 !important;

  :deep(.font-caption-2) {
    color: $text-color-tertiary;
    font-weight: 700;
    line-height: 24px;
  }

  :deep(.font-footnote-1) {
    color: $success-default;
    font-weight: 400;
    line-height: 24px;
  }

  .password-checked {
    color: $success-default;
  }

  .icon-check,
  .icon-dot {
    width: 24px;
    height: 24px;
  }
}
</style>
